import RichText from 'components/UI/Richtext';
import CTA from 'components/UI/Cta';
import { ReactElement, useContext } from 'react';
import {
  IComponentVideoLeftTextRightFields,
  IFragmentContentfulHostedVideo,
  IFragmentRendiaVideo,
  IFragmentVimeoVideo,
  IFragmentYouTubeVideo,
  IFragmentFreshpaintVideo,
} from 'types/contentful';
import { LINK_CONTENT_TYPE } from 'lib/consts';
import IFrameCTA from 'components/UI/IFrameCta';
import { useRouter } from 'next/router';
import SiteDataContext from 'context/SiteDataContext';
import Vimeo, { isVimeoVideoGuard } from 'components/UI/Videos/Vimeo';
import FreshpaintVideo, { isFreshpaintVideoGuard } from 'components/UI/Videos/Freshpaint';
import YouTube, { isYoutubeVideoGuard } from 'components/UI/Videos/Youtube';
import Rendia, { isRendiaVideoGuard } from 'components/UI/Videos/Rendia';
import ContentfulVideo from 'components/UI/Videos/ContentfulVideo';

export enum VideoTypeId {
  Vimeo = 'fragmentVimeoVideo',
  Youtube = 'fragmentYouTubeVideo',
  Contentful = 'fragmentContentfulHostedVideo',
  Rendia = 'fragmentRendiaVideo',
  Freshpaint = 'fragmentFreshpaintVideo',
}

export type VideoTypes =
  | IFragmentContentfulHostedVideo
  | IFragmentVimeoVideo
  | IFragmentYouTubeVideo
  | IFragmentRendiaVideo
  | IFragmentFreshpaintVideo;

function fullUrl(domain: string, path: string): URL {
  const protocol = domain.startsWith('localhost') ? 'http' : 'https';

  return new URL(`${protocol}://${domain}${path}`);
}

interface VideoLeftTextRightProps extends IComponentVideoLeftTextRightFields {
  className?: string | undefined;
  textAlignment?: 'left' | 'right';
}

export default function VideoLeftTextRight({
  id,
  heading,
  video,
  backgroundColor,
  description,
  cta,
  textAlignment = 'right',
  className = '',
}: VideoLeftTextRightProps): ReactElement {
  const router = useRouter();
  const { domain } = useContext(SiteDataContext);

  const Video = (): ReactElement => {
    if (isVimeoVideoGuard(video)) {
      return <Vimeo video={video} />;
    } else if (isYoutubeVideoGuard(video)) {
      return <YouTube video={video} />;
    } else if (isRendiaVideoGuard(video)) {
      return <Rendia video={video} url={fullUrl(domain, router.asPath)} />;
    } else if (isFreshpaintVideoGuard(video)) {
      return <FreshpaintVideo video={video} />;
    } else {
      return <ContentfulVideo video={video} />;
    }
  };

  const Content = (): ReactElement => (
    <div className="lg:w-1/2 flex flex-col gap-4 lg:gap-8 relative -mt-2">
      {heading && (
        <h2 className="bordered-heading font-heading text-new-34 lg:text-4xl lg:leading-new-43 font-light">
          {heading}
        </h2>
      )}
      {description && (
        <RichText document={description} classNames="text-lg richtext-font-inherit" />
      )}
      {cta && (
        <div>
          {cta.sys.contentType.sys.id === LINK_CONTENT_TYPE.fragmentIFrameCta ? (
            <IFrameCTA {...cta.fields} backgroundColor="black" classNames="w-full md:w-auto" />
          ) : (
            <CTA
              {...cta.fields}
              classNames="text-primary-actual button-text bg-white w-full md:w-auto"
            />
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={`ecp-my ${className}`} id={id}>
      <div
        className={`px-site-component bg-${backgroundColor} text-primary-actual ${
          backgroundColor === 'white' ? 'pb-10 md:pb-20' : 'py-10 md:py-20'
        }`}
      >
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
          {textAlignment === 'left' && <Content />}
          <div className={`lg:w-1/2`}>
            <div className={`inline-block w-full`}>
              <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                <Video />
              </div>
            </div>
          </div>
          {textAlignment === 'right' && <Content />}
        </div>
      </div>
    </div>
  );
}
